import posthog, { type CaptureResult } from 'posthog-js';
import { fetchGlobalPreference } from '@common/api/rest';
import store from '@common/store';
import {
  type BetaFeatureKey,
  type Feature,
  isBetaFeature,
  setFeatureFlags,
  type validityCheckFlag,
  type Variants,
} from '@common/store/reducers/featureFlags';
import type User from '@common/types/models/User';
import { UserState } from '@common/types/store';
import { GLOBAL_PREFERENCE_FEATURE_FLAG_KEY } from '@common/utils/storage/storageKeys';

type Action = (enabled: boolean) => void;

const actions: Action[] = [];

type FallbackValues = Partial<Record<Feature, true>> & { [validityCheckFlag]: true; };
export const finalFallbackValues: FallbackValues = {
  FLAGS_ARE_VALID: true,
};

export const getFallbackValues = async (): Promise<Variants> => {
  try {
    const values = await fetchGlobalPreference(GLOBAL_PREFERENCE_FEATURE_FLAG_KEY);
    return JSON.parse(
      values?.replace(/\s/g, '') ?? JSON.stringify(finalFallbackValues),
    ) as Variants;
  } catch {
    return finalFallbackValues;
  }
};

// Identity events are sent by identifyUser, which at startup runs while the user state is still
// Unknown; a recording with snapshots missing cannot be replayed (a reset stops a stale one)
const alwaysSentEvents = ['$identify', '$set', '$snapshot'];

// Until the current user has been checked PostHog may still hold an expired session's user, so
// other events are dropped (not delayed)
const dropWhileUserUnknown = (
  event: CaptureResult | null,
) => (event && !alwaysSentEvents.includes(event.event)
    && store.getState().main.userState === UserState.Unknown
  ? null
  : event);

const initialize = () => {
  getFallbackValues().then((flags) => {
    store.dispatch(setFeatureFlags({ featureFlags: flags }));
  });

  if (process.env.POSTHOG_TOKEN) {
    posthog.init(
      process.env.POSTHOG_TOKEN,
      {
        autocapture: process.env.POSTHOG_AUTOCAPTURE_ENABLED === 'true'
          ? {
            dom_event_allowlist: ['click', 'submit'],
            url_ignorelist: ['/device/.+?/diagram'],
          }
          : false,
        api_host: `https://${process.env.POSTHOG_API_HOST}`,
        disable_session_recording: true,
        capture_pageview: false,
        person_profiles: 'identified_only',
        before_send: dropWhileUserUnknown,
      },
    );
    posthog.onFeatureFlags((flags: string[], variants: Variants, { errorsLoading } = {}) => {
      store.dispatch(setFeatureFlags({ featureFlags: variants, flagsHaveAuthority: true }));

      // A failed load replays the stored flags, so only a real answer may switch things off
      const isRealAnswer = variants.FLAGS_ARE_VALID && !errorsLoading;
      if (variants.SessionRecording) {
        posthog.startSessionRecording();
      } else if (isRealAnswer) {
        // Ends a recording an earlier user's flags started
        posthog.stopSessionRecording();
      }
      if (variants.FLAGS_ARE_VALID && (variants.EnableTranslationLogger || isRealAnswer)) {
        actions.forEach((action) => action(!!variants.EnableTranslationLogger));
      }
    });
  }
};

const cleanup = () => {
  posthog.reset();
  if (posthog.__loaded) {
    // Started by the previous user's flags; the next user's flags start it again if needed
    posthog.stopSessionRecording();
    // A flags answer still in flight for the previous user would otherwise be the last one applied
    posthog.reloadFeatureFlags();
  }
};

const identifyUser = (user: User) => {
  // Logout's reset is a no-op for a session ended before PostHog loads; without this the previous
  // user's person properties (e.g. beta opt-ins) would go with this user's flag request
  if (
    posthog.get_property('$user_state') === 'identified' && posthog.get_distinct_id() !== user.id
  ) {
    cleanup();
  }
  posthog.identify(user.id, {
    email: user.email,
    language: user.language,
    role: user.userLevelName,
    company: user.company || user.retailerId || '-', // NOSONAR:S6606 - Empty strings should fallback
    app_version: process.env.APP_VERSION,
  });
};

const captureEvent = (eventName: string, payload?: Record<string, any>) => {
  posthog.capture(eventName, payload);
};

export type BetaFeature = {
  flagKey: BetaFeatureKey;
  name: string;
  description: string;
  enrolled: boolean;
};

export const BETA_FEATURES_TIMEOUT_MS = 3 * 1000;

// Resolves undefined when PostHog is unavailable, slow or answers with something unexpected.
const getBetaFeatures = () =>
  new Promise<BetaFeature[] | undefined>((resolve) => {
    if (!posthog.__loaded) {
      resolve(undefined);
      return;
    }
    const timeout = setTimeout(() => resolve(undefined), BETA_FEATURES_TIMEOUT_MS);
    // Beta is requested explicitly so we don't depend on the server's default stage.
    posthog.getEarlyAccessFeatures(
      (features) => {
        clearTimeout(timeout);
        if (!Array.isArray(features)) {
          resolve(undefined);
          return;
        }
        try {
          resolve(features.flatMap(({ flagKey, name, description }) =>
            isBetaFeature(flagKey)
              ? [{
                flagKey,
                name,
                description,
                enrolled: posthog.isFeatureEnabled(flagKey, { send_event: false }) === true,
              }]
              : []
          ));
        } catch (error) {
          // The timeout is already cleared, so settle here or the page would load forever
          console.error('Failed to read beta features:', error);
          resolve(undefined);
        }
      },
      true,
      ['beta'],
    );
  });

const setBetaFeatureEnrollment = (flagKey: BetaFeatureKey, enrolled: boolean) =>
  posthog.updateEarlyAccessFeatureEnrollment(flagKey, enrolled);

const analytics = {
  getBetaFeatures,
  setBetaFeatureEnrollment,
  cleanup,
  initialize,
  identifyUser,
  captureEvent,
  addActionForEnableTranslationLogger: (listener: Action) => actions.push(listener),
};

export { default as usePageViewTracker } from '@common/utils/analytics/usePageViewTracker';

export default analytics;
